import { createPersistentComponentState } from '@/state';
import { customLocalStorage } from '@/state/storage';

/**
 * AppState holds application-level UI preferences and transient page state.
 *
 * - `settings`: component-level UI preferences (e.g. panel positions), keyed by component name.
 * - `page`: transient per-page state (e.g. last query), keyed by page name.
 *
 * Previously managed by the Redux `appReducer`; now backed by a Zustand persistent store.
 */
type AppState = {
  settings: Record<string, any>;
  page: Record<string, any>;
};

const initAppState: AppState = {
  settings: {},
  page: {},
};

/**
 * Zustand persistent store for application-level state.
 * Data is persisted to localStorage under the key `"app"`.
 *
 * Prefer `useAppState` (hook) or `appState` (outside components) over accessing this store directly.
 */
const useAppStore = createPersistentComponentState('app', initAppState, {
  storage: customLocalStorage,
});

/**
 * Hook for reading and writing application-level state inside React components.
 *
 * Returns a `[state, setState]` tuple that mirrors the previous Redux-based `useAppState` API,
 * making it a drop-in replacement with no changes required at call sites.
 *
 * @param selector - optional selector to derive a slice of the state (defaults to the full state).
 * @returns `[selectedState, setState]`
 *
 * @example
 * // Read a setting
 * const [{ settings }] = useAppState();
 * const position = settings?.myComponent?.previewPosition;
 *
 * @example
 * // Write a setting
 * const [{ settings }, setAppState] = useAppState();
 * setAppState((current) => ({ ...current, settings: { ...current.settings, myComponent: { previewPosition: 'top' } } }));
 */
const useAppState = (selector: (fullState: AppState) => any = (s: AppState): AppState => s): [any, Function] => {
  const storeState = useAppStore(({ state }) => state);
  const setState = useAppStore(({ setState }) => setState);
  const selected = selector(storeState);
  return [selected, setState];
};

/**
 * Functional accessor for application-level state, for use **outside** React components.
 *
 * @param selector - optional selector to derive a slice of the state (defaults to the full state).
 * @returns `[selectedState, setState]`
 */
const appState = (selector: (fullState: AppState) => any = (s: AppState): AppState => s): [any, Function] => {
  const { state, setState } = useAppStore.getState();
  const selected = selector(state);
  return [selected, setState];
};

export { useAppState, appState };
export default useAppStore;
